// ShaniReportPage.js — Shani Analysis paid PDF report viewer
// Accessed via: vedsanga.com/?shani_id=xxx&token=yyy (WhatsApp payment link)
import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// Matches JyotishPage.js's JT token system — white, one accent, plain Inter.
const RT = {
  page:     "#ffffff",
  card:     "#ffffff",
  border:   "#e2e2e2",
  shadow:   "0 1px 3px rgba(0,0,0,0.08)",
  font:     "'Inter',sans-serif",
  text:     "#1a1a1a",
  textDim:  "#5a5a5a",
  accent:   "#c2560f",
};

const pdfSafe = s => String(s)
  .replace(/—|–/g, "-").replace(/•|·/g, "-")
  .replace(/['']/g, "'").replace(/[""]/g, '"')
  .replace(/[^\x00-\xFF]/g, "").trim();

function downloadShaniPDF(report) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const W = 210, M = 15, CW = W - 2 * M;
  let y = 0;
  const newPage = () => { doc.addPage(); y = 22; };
  const checkY  = (need = 10) => { if (y + need > 278) newPage(); };

  // Header bar — accent matching the site's single accent color
  doc.setFillColor(194, 86, 15);
  doc.rect(0, 0, W, 14, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text("VedSanga  -  Vedic Astrology & Jyotish", M, 9);
  doc.text("Shani Analysis Report", W - M, 9, { align: "right" });

  // Title
  y = 22;
  doc.setFont("helvetica", "bold"); doc.setFontSize(20);
  doc.setTextColor(26, 26, 26);
  doc.text("Shani Analysis", M, y); y += 9;

  // Divider
  doc.setDrawColor(226, 226, 226); doc.setLineWidth(0.6);
  doc.line(M, y, W - M, y); y += 7;

  // Meta block
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const meta = [
    `Name: ${pdfSafe(report.name || "")}   |   Moon Rashi: ${pdfSafe(report.rashi || "")}   |   Nakshatra: ${pdfSafe(report.nakshatra || "")}`,
    `Lagna: ${pdfSafe(report.lagna || "Not provided")}   |   Maha Dasha: ${pdfSafe(report.maha_dasha || "")}`,
    `Generated: ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}`,
  ];
  meta.forEach(l => { doc.text(pdfSafe(l), M, y); y += 5.5; });
  y += 3;
  doc.setDrawColor(226, 226, 226); doc.setLineWidth(0.3);
  doc.line(M, y, W - M, y); y += 9;

  // Sections
  (report.sections || []).forEach(sec => {
    checkY(22);
    doc.setFillColor(194, 86, 15);
    doc.roundedRect(M, y - 6, CW, 10, 2, 2, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(pdfSafe(sec.heading), M + 4, y); y += 8;

    (sec.lines || []).forEach(line => {
      checkY(8);
      if (line === "---") {
        doc.setDrawColor(226, 226, 226); doc.setLineWidth(0.2);
        doc.line(M + 2, y - 2, W - M - 2, y - 2); y += 4;
      } else {
        const raw    = pdfSafe(String(line));
        const bullet = raw.startsWith("-") || raw.startsWith("  ");
        doc.setFont("helvetica", "normal"); doc.setFontSize(10);
        doc.setTextColor(bullet ? 60 : 26, bullet ? 60 : 26, bullet ? 60 : 26);
        const wrapped = doc.splitTextToSize(raw, CW - (bullet ? 8 : 2));
        wrapped.forEach(wl => { checkY(7); doc.text(wl, M + (bullet ? 4 : 2), y); y += 5.5; });
      }
    });
    y += 6;
  });

  // Footer
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(120, 120, 120);
    doc.text("VedSanga - For spiritual & educational guidance only. Consult a qualified Jyotishi for important decisions.", M, 291);
    doc.text(`Page ${i} / ${total}`, W - M, 291, { align: "right" });
  }

  doc.save(`VedSanga_Shani_${pdfSafe(report.name || "Report")}.pdf`);
}

function SectionCard({ sec }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ marginBottom: 14, borderRadius: 14, overflow: "hidden",
      border: `1px solid ${RT.border}`, background: RT.card, boxShadow: RT.shadow }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: "100%", textAlign: "left", padding: "14px 18px",
          background: "#fafafa",
          border: "none", borderBottom: open ? `1px solid ${RT.border}` : "none", cursor: "pointer", display: "flex",
          justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: RT.font, fontSize: "1.05rem",
          color: RT.text, fontWeight: 700 }}>{sec.heading}</span>
        <span style={{ color: RT.accent, fontSize: "1.1rem" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ padding: "12px 18px 18px" }}>
          {(sec.lines || []).map((line, i) => {
            if (line === "---") return <hr key={i} style={{ border: "none",
              borderTop: `1px solid ${RT.border}`, margin: "8px 0" }} />;
            if (!line) return <div key={i} style={{ height: 8 }} />;
            const isBullet = line.startsWith("-") || line.startsWith("  ");
            return (
              <div key={i} style={{
                fontFamily: RT.font, fontSize: "1.05rem",
                color: isBullet ? RT.textDim : RT.text,
                lineHeight: 1.7, paddingLeft: isBullet ? 12 : 0, marginBottom: 2,
              }}>{line}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ShaniReportPage({ shaniId, token }) {
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!shaniId) return;
    fetch(`${API}/jyotish/shani/report/${shaniId}?token=${encodeURIComponent(token || "")}`)
      .then(r => {
        if (r.status === 402) throw new Error("Payment not confirmed yet. Please wait a moment and refresh.");
        if (r.status === 403) throw new Error("Invalid link. Please use the exact link sent to your WhatsApp.");
        if (r.status === 404) throw new Error("Report not found. Please contact support.");
        return r.json();
      })
      .then(d => { setReport(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [shaniId, token]);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: RT.page, display: "flex",
      alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <div style={{ fontSize: "2rem" }}>🪐</div>
      <div style={{ color: RT.text, fontFamily: RT.font, fontSize: "1.05rem" }}>
        Loading your Shani report...
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: "100vh", background: RT.page, display: "flex",
      alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ textAlign: "center", color: "#b91c1c", maxWidth: 340 }}>
        <div style={{ fontSize: "2rem", marginBottom: 12 }}>⚠️</div>
        <div style={{ fontFamily: RT.font, fontSize: "1.1rem", lineHeight: 1.6 }}>
          {error}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: RT.page, color: RT.text,
      fontFamily: RT.font }}>
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "24px 16px 48px" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontFamily: RT.font, fontSize: "0.95rem", fontWeight: 600,
            color: RT.accent, marginBottom: 8 }}>VedSanga · Vedic Astrology</div>
          <div style={{ fontFamily: RT.font, fontSize: "1.6rem", fontWeight: 700,
            color: RT.text, marginBottom: 6 }}>
            Shani Analysis Report
          </div>
          <div style={{ color: RT.textDim, fontSize: "1.05rem" }}>
            {report.name} · {report.rashi} · {report.nakshatra}
          </div>
        </div>

        {/* Download button */}
        <button onClick={() => downloadShaniPDF(report)}
          style={{ width: "100%", padding: "15px 20px", borderRadius: 14, border: "none",
            background: RT.accent, cursor: "pointer",
            color: "#ffffff", fontFamily: RT.font, fontSize: "1.05rem",
            fontWeight: 700, marginBottom: 24,
            boxShadow: "0 4px 20px rgba(194,86,15,0.35)" }}>
          📥 Download PDF Report
        </button>

        {/* Sections */}
        {(report.sections || []).map((sec, i) => (
          <SectionCard key={i} sec={sec} />
        ))}

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 32, color: RT.textDim,
          fontSize: "0.95rem", lineHeight: 1.7 }}>
          For spiritual & educational guidance only.<br />
          Consult a qualified Jyotishi for important decisions.<br />
          <span style={{ color: RT.accent, fontWeight: 600 }}>VedSanga · vedsanga.com</span>
        </div>
      </div>
    </div>
  );
}
